import React, { useState, useEffect } from "react";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import Button from "../../../shared/components/FormElements/Button";
import {
	ArrowLeftIcon,
	ShieldCheckIcon,
	CheckCircleIcon,
	ExclamationTriangleIcon,
	UsersIcon,
} from "@heroicons/react/20/solid";
import FreeTrialOrderSummary from "./FreeTrialOrderSummary";
import { CheckIcon } from "@heroicons/react/16/solid";

const Step2 = ({ formikProps, clientSecret, onBack, planInfo }) => {
	const stripe = useStripe();
	const elements = useElements();
	const [isProcessing, setIsProcessing] = useState(false);
	const [paymentError, setPaymentError] = useState(null);
	const [showSuccess, setShowSuccess] = useState(false);
	const [paymentReady, setPaymentReady] = useState(false);

	const { values, validateForm } = formikProps;

	// Extract coupon code from URL for display
	const [appliedCode, setAppliedCode] = useState(null);
	useEffect(() => {
		try {
			const params = new URLSearchParams(window.location.search || "");
			const c =
				params.get("coupon") ||
				params.get("promo") ||
				params.get("discount");
			if (c && typeof c === "string" && c.trim() !== "") {
				setAppliedCode(c.trim());
			}
		} catch (_) {}
	}, []);

	const addressComplete = Boolean(
		values.address && values.city && values.postalCode,
	);

	// Track payment element readiness
	useEffect(() => {
		try {
			if (
				paymentReady &&
				typeof window !== "undefined" &&
				typeof window.datafast === "function"
			) {
				window.datafast("payment_element_ready", {
					planName: planInfo?.name || null,
					trialDays: planInfo?.trialDays ?? null,
				});
			}
		} catch (_) {}
	}, [paymentReady, planInfo?.name, planInfo?.trialDays]);

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!stripe || !elements) {
			console.error("Stripe of Elements niet geladen");
			setPaymentError(
				"Betalingssysteem nog niet geladen. Probeer het opnieuw.",
			);
			return;
		}

		// Validate form first
		const formErrors = await validateForm();
		if (Object.keys(formErrors).length > 0) {
			console.error("Form validation errors:", formErrors);
			setPaymentError("Vul alle verplichte velden correct in");
			return;
		}

		// Check if address fields are filled
		if (!values.address || !values.city || !values.postalCode) {
			setPaymentError("Vul alle adresgegevens in");
			return;
		}

		// Check if client secret is available
		if (!clientSecret) {
			console.error("No client secret available");
			setPaymentError(
				"Betalingsinformatie ontbreekt. Ververs de pagina en probeer opnieuw.",
			);
			return;
		}

		setIsProcessing(true);
		setPaymentError(null);

		try {
			console.log("Starting payment confirmation...");

			// Track submit start
			try {
				if (
					typeof window !== "undefined" &&
					typeof window.datafast === "function"
				) {
					window.datafast("payment_submit", {
						planName: planInfo?.name || null,
						trialDays: planInfo?.trialDays ?? null,
						addressComplete,
					});
				}
			} catch (_) {}

			// First, submit and validate the payment element
			const { error: submitError } = await elements.submit();
			if (submitError) {
				console.error("Payment element submit error:", submitError);
				setPaymentError(
					submitError.message ||
						"Fout bij het voorbereiden van de betaling",
				);
				try {
					if (
						typeof window !== "undefined" &&
						typeof window.datafast === "function"
					) {
						window.datafast("payment_submit_error", {
							type: submitError.type || "unknown",
							message: submitError.message || null,
						});
					}
				} catch (_) {}
				setIsProcessing(false);
				return;
			}

			// Then confirm the payment
			const { error } = await stripe.confirmSetup({
				elements,
				clientSecret,
				confirmParams: {
					return_url: `${window.location.origin}/betaling/succes`,
					payment_method_data: {
						billing_details: {
							name: `${values.firstName} ${values.lastName}`,
							email: values.email,
							address: {
								line1: values.address,
								city: values.city,
								postal_code: values.postalCode,
								country: "NL",
							},
						},
					},
				},
				redirect: "if_required",
			});

			if (error) {
				console.error("Payment confirmation error:", error);

				// Handle different types of errors
				if (
					error.type === "card_error" ||
					error.type === "validation_error"
				) {
					setPaymentError(error.message);
				} else {
					setPaymentError(
						"Er is een fout opgetreden bij het verwerken van de betaling. Probeer het opnieuw.",
					);
				}
				try {
					if (
						typeof window !== "undefined" &&
						typeof window.datafast === "function"
					) {
						window.datafast("payment_confirm_error", {
							type: error.type || "unknown",
							message: error.message || null,
						});
					}
				} catch (_) {}
			} else {
				console.log("Payment confirmed successfully");
				setShowSuccess(true);
				try {
					if (
						typeof window !== "undefined" &&
						typeof window.datafast === "function"
					) {
						window.datafast("payment_confirm_success", {
							planName: planInfo?.name || null,
							trialDays: planInfo?.trialDays ?? null,
						});
					}
				} catch (_) {}

				// Redirect handled by success page or if_required path; do nothing here
			}
		} catch (err) {
			console.error("Payment processing error:", err);
			setPaymentError(
				"Er is een onverwachte fout opgetreden. Probeer het opnieuw.",
			);
			try {
				if (
					typeof window !== "undefined" &&
					typeof window.datafast === "function"
				) {
					window.datafast("payment_unexpected_error", {
						message: err?.message || null,
					});
				}
			} catch (_) {}
		} finally {
			setIsProcessing(false);
		}
	};

	// Pricing details are shown via FreeTrialOrderSummary using planInfo

	return (
		<div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in [&>*:first-child]:lg:order-none [&>*:first-child]:order-last">
			{/* Left Column - Form and Payment */}
			<div className="lg:col-span-2 space-y-6 bg-white border border-slate-200 rounded-2xl shadow-sm p-8 transform transition-all duration-500 hover:shadow-md">
				<div className="mb-8 ">
					<h1 className="text-2xl font-bold text-gray-900 mb-2 animate-slide-up">
						Bijna klaar!
					</h1>
					<p
						className="text-lg text-gray-600 animate-slide-up"
						style={{ animationDelay: "0.1s" }}
					>
						Vul je factuurgegevens in
					</p>
				</div>

				{paymentError && (
					<div className="bg-red-50 border border-red-200 rounded-lg p-4 animate-shake">
						<div className="flex items-center space-x-2">
							<ExclamationTriangleIcon className="size-5 text-red-600 flex-shrink-0" />
							<div className="text-red-800">{paymentError}</div>
						</div>
					</div>
				)}

				{showSuccess && (
					<div className="bg-green-50 border border-green-200 rounded-lg p-4 animate-slide-down">
						<div className="flex items-center space-x-2">
							<CheckCircleIcon className="size-5 text-green-600 flex-shrink-0" />
							<div className="text-green-800">
								Betaling succesvol verwerkt!
							</div>
						</div>
					</div>
				)}

				<form onSubmit={handleSubmit} className="space-y-6">
					{/* Payment Method */}
					<div className="transform transition-all">
						<div className="flex items-center mb-4 mt-12">
							<ShieldCheckIcon className="size-5 text-purple-600 mr-2" />
							<h3 className="text-lg font-semibold text-gray-900">
								Betaalmethode
							</h3>
						</div>

						<div className="border border-gray-200 rounded-lg p-4 ">
							<PaymentElement
								options={{
									business: { name: "DentHR" },
									layout: {
										type: "tabs",
									},
									paymentMethodOrder: [
										"ideal",
										"sepa_debit",
										"bancontact",
									],
									terms: {
										ideal: "never",
										bancontact: "never",
										sepa_debit: "never",
									},
									fields: {
										billingDetails: {
											name: "never",
											email: "never",
										},
									},
								}}
								onReady={() => setPaymentReady(true)}
							/>
						</div>
					</div>
					<div
						className="flex items-center animate-fade-in"
						style={{ animationDelay: "0.5s" }}
					>
						<span className="text-sm text-gray-500">
							Door te bevestigen ga je akkoord met onze{" "}
							<a
								href="https://dentiva.nl/algemene-voorwaarden/"
								className="text-purple-600 hover:underline transition-colors duration-200"
								target="_blank"
								rel="noopener noreferrer"
							>
								algemene voorwaarden
							</a>{" "}
							en{" "}
							<a
								href="https://dentiva.nl/privacy-cookies/"
								className="text-purple-600 hover:underline transition-colors duration-200"
								target="_blank"
								rel="noopener noreferrer"
							>
								privacybeleid
							</a>
						</span>
					</div>

					{/* {trialDays === 0 && (
					<div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
						<div className="flex items-center space-x-2">
							<ExclamationTriangleIcon className="size-5 text-amber-600 flex-shrink-0" />
							<div className="text-amber-800 text-sm">
								Geen proefperiode. De eerste betaling vindt
								direct plaats.
							</div>
						</div>
					</div>
					)} */}

					{/* Action buttons */}
					<div className="flex flex-col sm:flex-row gap-4 pt-4">
						<div className="hidden sm:block">
							<Button
								type="button"
								onClick={onBack}
								ghost
								size="large"
								iconPrefix={ArrowLeftIcon}
								disabled={isProcessing}
								classNames="transform transition-all duration-200 hover:scale-105"
							>
								Terug
							</Button>
						</div>

						<Button
							onClick={handleSubmit}
							size="large"
							fullWidth
							isLoading={isProcessing}
							disabled={
								!stripe || isProcessing || !addressComplete
							}
							iconSuffix={CheckIcon}
							classNames="transform transition-all duration-200 hover:scale-105 disabled:scale-100"
						>
							{isProcessing
								? "Verwerken..."
								: "Krijg toegang"}
						</Button>
						<div className="block sm:hidden w-full flex justify-center mx-auto">
							<button
								type="button"
								onClick={onBack}
								className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 text-center justify-center"
							>
								<ArrowLeftIcon className="size-4" />
								Terug
							</button>
						</div>
					</div>
					<div className="flex space-x-5 transform transition-all duration-300 items-center justify-center ">
						<div className="flex items-center gap-2">
							<ShieldCheckIcon className="size-5 text-green-600 flex-shrink-0" />
							<div>
								<p className="text-sm font-medium text-green-800">
									Opzeggen kan altijd met één klik
								</p>
							</div>
						</div>
						<div className="flex items-center gap-2">
							<UsersIcon className="size-5 text-green-600 flex-shrink-0" />
							<div>
								<p className="text-sm font-medium text-green-800">
									150+ praktijken gebruiken DentHR al
								</p>
							</div>
						</div>
					</div>
				</form>
			</div>

			{/* Right Column - Order Summary and Security */}
			<div className="lg:col-span-1 space-y-6">
				{/* Order summary */}
				<FreeTrialOrderSummary
					planInfo={planInfo}
					values={values}
					couponCode={appliedCode}
				/>
			</div>

			<style jsx>{`
				@keyframes fade-in {
					from {
						opacity: 0;
						transform: translateY(20px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}
				@keyframes slide-up {
					from {
						opacity: 0;
						transform: translateY(30px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}
				@keyframes slide-down {
					from {
						opacity: 0;
						transform: translateY(-20px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}
				@keyframes scale-in {
					from {
						transform: scale(0);
					}
					to {
						transform: scale(1);
					}
				}
				@keyframes shake {
					0%,
					100% {
						transform: translateX(0);
					}
					25% {
						transform: translateX(-5px);
					}
					75% {
						transform: translateX(5px);
					}
				}
				@keyframes bounce-slow {
					0%,
					100% {
						transform: translateY(0);
					}
					50% {
						transform: translateY(-10px);
					}
				}
				.animate-fade-in {
					animation: fade-in 0.6s ease-out;
				}
				.animate-slide-up {
					animation: slide-up 0.5s ease-out;
				}
				.animate-slide-down {
					animation: slide-down 0.3s ease-out;
				}
				.animate-scale-in {
					animation: scale-in 0.3s ease-out;
				}
				.animate-shake {
					animation: shake 0.5s ease-out;
				}
				.animate-bounce-slow {
					animation: bounce-slow 2s ease-in-out infinite;
				}
			`}</style>
		</div>
	);
};

export default Step2;
